import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import type { HolidayItem } from '@/api/domains/attendance';
import { useEditHolidayFormPanel } from '@/hooks/attendance/holiday/useEditHolidayFormPanel';


interface EditHolidayFormPanelProps {
  holidayData: HolidayItem;
  onSubmit?: () => void;
}

export const EditHolidayFormPanel: React.FC<EditHolidayFormPanelProps> = ({
  holidayData,
  onSubmit,
}) => {
  const { t: translate } = useTranslation();

  const {
    holidayId,
    holidayTypeValue,
    holidayTypeOptions,
    dateValue,
    reasonValue,
    nonWaivableValue,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
    handleHolidayTypeChange,
    handleDateChange,
    handleReasonChange,
    handleNonWaivableChange,
    handleSubmit,
  } = useEditHolidayFormPanel({ holidayData, onSubmit });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="edit-holiday-form-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:holiday.form.idLabel')}
          value={String(holidayId)}
          disabled
          readOnly
          data-testid="holiday-id-input"
        />

        <Selector
          label={translate('attendance:holiday.form.holidayType')}
          placeholder={translate('attendance:holiday.form.holidayTypePlaceholder')}
          required
          options={holidayTypeOptions}
          value={holidayTypeValue}
          onChange={handleHolidayTypeChange}
          disabled={isSubmitting}
          color={errors.holidayTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.holidayTypeId?.message && isSubmitted
              ? translate(String(errors.holidayTypeId.message))
              : undefined
          }
          data-testid="holiday-type-selector"
        />

        <DatePicker
          label={translate('attendance:holiday.form.date')}
          value={dateValue}
          onChange={handleDateChange}
          disabled={isSubmitting}
          color={errors.date && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.date?.message && isSubmitted
              ? translate(String(errors.date.message))
              : undefined
          }
          placeholder={translate('attendance:holiday.form.datePlaceholder')}
          required
          data-testid="holiday-date-picker"
        />

        <LabelInput
          label={translate('attendance:holiday.form.reason')}
          placeholder={translate('attendance:holiday.form.reasonPlaceholder')}
          value={reasonValue}
          onChange={handleReasonChange}
          required
          disabled={isSubmitting}
          color={errors.reason && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.reason?.message && isSubmitted
              ? translate(String(errors.reason.message))
              : undefined
          }
          data-testid="holiday-reason-input"
        />

        <Checkbox
          label={translate('attendance:holiday.form.nonWaivable')}
          checked={nonWaivableValue}
          onChange={handleNonWaivableChange}
          disabled={isSubmitting}
          data-testid="holiday-non-waivable-checkbox"
        />
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="edit">
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={!isReady || isSubmitting}
          >
            {translate('common:actions.save')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default EditHolidayFormPanel;
